import { useQuery } from '@tanstack/react-query';
import { Languages, Star } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Endpoint } from '@/Config/api/endpoint';
import { ConversationApi } from '@/Domain/api/Conversation/Conversation.api';
import { ConversationDto } from '@/Domain/api/Conversation/dto/Conversation.dto';
import { RatingDto } from '@/Domain/api/Conversation/dto/Rating.dto';
import { ConversationSourceIcon } from '@/Domain/components/Inbox/ConversationList/ConversationSourceIcon';
import { RatingPopover } from '@/Domain/components/Inbox/RatingPopover/RatingPopover';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import {
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenu,
  DropdownMenuCheckboxItem,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Domain/components/Shared/Popover/Popover';
import { ConversationHelper } from '@/Domain/helper/Conversation.helper';
import { useConversationTranslation } from '@/Domain/hooks/Conversation/useConversationTranslate/useConversationTranslation.hook';

type Props = {
  id: string;
  conversation: ConversationDto;
};

export const ConversationActionBar = ({ conversation, id }: Props) => {
  const { t, i18n } = useTranslation('inbox_v2');
  const translationLanguage = i18n.resolvedLanguage ?? i18n.language ?? 'en';

  const enabled = Boolean(conversation?.id && translationLanguage);
  const { data: ratingData, refetch: refetchRatingData } = useQuery<RatingDto[]>({
    queryKey: [Endpoint.conversation.rating, conversation?.id, translationLanguage],
    queryFn: () => ConversationApi.rating(conversation!.id, translationLanguage),
    enabled,
  });

  const wasRatingRecentlyFetchedRef = useRef<boolean>(false);
  const ratingDebounceTimerRef = useRef<number | null>(null);
  const ratingDebounceTimerLimit = 5000;

  const refetchWithThrottle = useCallback(() => {
    if (wasRatingRecentlyFetchedRef.current) return;
    wasRatingRecentlyFetchedRef.current = true;
    void refetchRatingData();

    ratingDebounceTimerRef.current = window.setTimeout(() => {
      wasRatingRecentlyFetchedRef.current = false;
      ratingDebounceTimerRef.current = null;
    }, ratingDebounceTimerLimit);
  }, [refetchRatingData, ratingDebounceTimerRef]);

  useEffect(
    () => () => {
      if (ratingDebounceTimerRef.current) {
        clearTimeout(ratingDebounceTimerRef.current);
        ratingDebounceTimerRef.current = null;
      }
    },
    [],
  );

  const currentLanguage = conversation?.most_frequent_language;
  const hasRating = ConversationHelper.hasRating(ratingData);

  const {
    language,
    isTranslationInProgress,
    handelChangeLanguage,
    LANGUAGES_OPTIONS,
    DEFAULT_LANGUAGE_OPTION,
  } = useConversationTranslation({
    conversationId: id,
  });

  return (
    <div className="flex items-center justify-between pb-4.5">
      <div className="flex flex-row md:flex-row items-center gap-2">
        <Badge
          variant="primary"
          size="lg"
          startIcon={
            <ConversationSourceIcon
              source={conversation.source}
              colorIconsType="inherit"
              fontAwesomeProps={{ fixedWidth: true }}
            />
          }
        >
          {conversation?.title}
        </Badge>

        <DropdownMenu>
          <DropdownMenuTrigger>
            <Badge
              variant="secondary"
              size="lg"
              className="cursor-pointer"
              startIcon={<Languages className="size-4" />}
            >
              {isTranslationInProgress.isTranslating &&
              isTranslationInProgress.conversationId === id
                ? t('translating')
                : language
                  ? t('translated')
                  : t('languages:original')}
            </Badge>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuCheckboxItem
              checked={!language}
              data-testid={`language-original`}
              onClick={() => handelChangeLanguage(DEFAULT_LANGUAGE_OPTION)}
            >
              {t('languages:original')}
            </DropdownMenuCheckboxItem>
            {LANGUAGES_OPTIONS.map(({ value, label }) => (
              <DropdownMenuCheckboxItem
                checked={!!(language && value.includes(language))}
                key={value}
                data-testid={`language-${value}`}
                disabled={currentLanguage ? value.includes(currentLanguage || '') : false}
                onClick={() => handelChangeLanguage(value)}
              >
                {label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Popover>
          <PopoverTrigger onMouseEnter={refetchWithThrottle}>
            <Badge
              size="lg"
              variant="secondary"
              className="cursor-pointer"
              startIcon={<Star className={`size-3 ${hasRating ? 'fill-current' : ''}`} />}
            />
          </PopoverTrigger>

          <PopoverContent>
            <RatingPopover rating={ratingData} hasRating={hasRating} />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
